/**
 * FloorCalendar — stage A of the PM-directed EXACT-UX port (2026-08-08, Greg):
 * the screen now renders the Manager Calendar prototype ITSELF (a patched copy
 * at app/public/floor-board/board.dc.html, chromeless), so layout, brand and
 * behavior are the prototype's own, byte-faithful — deliberately NOT the app
 * design system (sanctioned exception, docs/design-baseline.md).
 *
 * The serving path still holds: this screen fetches the day through the same
 * adapter ops as before (useFloorCalendarDay) and POSTS the mapped tenant shape
 * (toDCData) into the board; the board's studio:navigate events relay back and
 * become real router navigation (Event Day / Concierge). Board-internal writes
 * are prototype-local this stage; they swap to real ops as the verbatim TSX
 * port replaces the embed screen-by-screen (stages B+).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { EmptyState } from "../../shell/composites";
import { useNav } from "../../shell/router";
import { useT } from "../../lib/i18n";
import { staffKey, toDCData, useFloorCalendarDay, cancelAppointment, checkInAppointment, markAppointmentNoShow, moveQueueDuty } from "./floor-calendar-data";
import { rescheduleAppointment, bookAppointment } from "../appointments/appointments-api";
import { listEventStaffing, assignEventRole } from "../workforce/wf-api";
import { calendarDay } from "@v0y4ge/api-client";
import { addQueueEntry } from "../queues/queues-api";

const hm = (min: number) => String(Math.floor(min / 60)).padStart(2, "0") + ":" + String(min % 60).padStart(2, "0");

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoPlus = (off: number) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + off);
  return d.toISOString().slice(0, 10);
};

export default function FloorCalendar() {
  const t = useT();
  const nav = useNav();
  const [dateISO] = useState(isoToday);
  /* The board owns which day is on screen; the host follows it so every day's
   * served families are read for THAT day rather than pinning today. */
  const [dayOff, setDayOff] = useState(0);
  const dateForDay = useMemo(() => (dayOff === 0 ? dateISO : isoPlus(dayOff)), [dayOff, dateISO]);
  const { day, loading, error, reload } = useFloorCalendarDay(dateForDay);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  /* board-local block id → served block id, for deletes of blocks created this session */
  const blockIdsRef = useRef<Record<string, string>>({});
  const [boardReady, setBoardReady] = useState(false);
  /* Public assets resolve through __STUDIO_ASSET_BASE__ (app/public), same
   * mechanism as JrniMark — a route-relative src would resolve against
   * /floor/calendar and 404. Falls back to the Vite site-root convention. */
  const boardSrc = useMemo(() => {
    const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
    return (base ? base.replace(/\/$/, "") + "/" : "/") + "floor-board/board.dc.html?chromeless=1";
  }, []);

  const push = useCallback(() => {
    if (!day || !frameRef.current || !frameRef.current.contentWindow) return;
    frameRef.current.contentWindow.postMessage({ type: "floor-data", data: toDCData(day, dayOff) }, window.location.origin);
  }, [day, dayOff]);

  /* ── stage C wave 1 (2026-08-10): the board posts write INTENTS after applying
   * its exact local behavior; this host calls the REAL op (the waves-1–4 wiring),
   * reloads the served day on success, and answers floor-write-error on failure
   * so the board can say so. "ap{n}" entry ids resolve through the payload's
   * bookIds (same order as the pushed rows). Manager-created entries have no
   * served identity yet (booking write is a later wave). */
  const writeBack = useCallback(async (m: { op?: string; id?: string; localId?: string; next?: string; off?: number; from?: number; to?: number; dur?: number; cancel?: boolean; name?: string; who?: string; what?: string; service?: string; queueId?: string; staffIdx?: number; eventId?: string; room?: string; staffName?: string; purpose?: string }) => {
    const fail = (message: string) => frameRef.current?.contentWindow?.postMessage({ type: "floor-write-error", message }, window.location.origin);
    const payload = day ? (toDCData(day, dayOff) as { bookIds?: string[]; dutyIds?: (string | null)[]; book?: unknown[][] }) : {};
    const servedId = (entryId: string): string | null => {
      if (!entryId.startsWith("ap")) return null;
      return (payload.bookIds || [])[Number(entryId.slice(2))] ?? null;
    };
    const staffOf = (i?: number) => (i != null && day ? day.staff[i] ?? null : null);
    const dateOf = (off?: number) => ((Number(off) || 0) === dayOff ? dateForDay : isoPlus(Number(off) || 0));
    try {
      if (m.op === "apptStatus") {
        const id = servedId(String(m.id || ""));
        if (!id) return fail("That entry was created on the board this session — it has no server booking to update yet");
        if (m.next === "now") { await checkInAppointment(id); }
        else if (m.next === "noshow") { await markAppointmentNoShow(id); }
        else { return fail("Revoking a status has no server operation yet — the change is local only (REQ-CAL-7)"); }
        reload();
        return;
      }
      if (m.op === "apptEdit") {
        const id = servedId(String(m.id || ""));
        if (!id) return fail("That entry was created on the board this session — it has no server booking to reschedule yet");
        const date = dateOf(m.off);
        await rescheduleAppointment(id, { date, start: hm(Number(m.from) || 0), end: hm((Number(m.from) || 0) + (Number(m.dur) || 30)), reason: "Rescheduled on the Floor Calendar" });
        reload();
        return;
      }
      if (m.op === "apptMove") {
        const id = servedId(String(m.id || ""));
        if (!id) return fail("That entry was created on the board this session — it has no server booking to move yet");
        const st = staffOf(m.staffIdx);
        await rescheduleAppointment(id, { date: dateForDay, start: hm(Number(m.from) || 0), end: hm((Number(m.from) || 0) + (Number(m.dur) || 30)), staffId: st?.staffId ?? null, staffName: st?.name ?? null, reason: "Moved on the Floor Calendar" });
        reload();
        return;
      }
      if (m.op === "book") {
        const st = staffOf(m.staffIdx);
        await bookAppointment({
          service: String(m.what || "Appointment"), date: dateOf(m.off),
          start: hm(Number(m.from) || 0), end: hm((Number(m.from) || 0) + (Number(m.dur) || 30)),
          staffId: st?.staffId ?? null, staffName: st?.name ?? null,
          locationId: day?.locationId ?? null,
          customer: { name: String(m.who || "Walk-in") }, origin: "floor-calendar",
        });
        /* The board drew an optimistic row the moment the manager saved; the served
         * day now carries the SAME booking too. The board retires its own local copy
         * when it recognises the served row (see the floor-data reconcile) — no ack
         * from here, so a booking the server does NOT return stays on the board
         * rather than vanishing (2026-09-03). */
        await reload();
        return;
      }
      if (m.op === "apptCancel") {
        /* Undo on the creation toast. The booking may have no board id (it was
         * created this session), so resolve it the way the board recognises its
         * own row: staff + start + duration + customer. Service name is NOT a
         * key — the server's vocabulary drifts from the board's. */
        const norm = (s: unknown) => String(s ?? "").trim().toLowerCase();
        const rows = payload.book || [];
        const i = rows.findIndex((r) =>
          Number(r[0]) === Number(m.staffIdx) && Number(r[1]) === Number(m.from) &&
          Number(r[2]) === Number(m.dur) && norm(r[3]) === norm(m.who));
        const id = i > -1 ? (payload.bookIds || [])[i] ?? null : null;
        if (!id) return fail("That booking never reached the server — it has been taken off the board only");
        await cancelAppointment(id, "Undone on the Floor Calendar");
        await reload();
        return;
      }
      if (m.op === "blockAdd") {
        const date = dateOf(m.off);
        const created = await calendarDay.createCalendarDayBlock({
          title: String(m.purpose || "Calendar block"),
          startsAt: date + "T" + hm(Number(m.from) || 0) + ":00",
          endsAt: date + "T" + hm((Number(m.from) || 0) + (Number(m.dur) || 30)) + ":00",
          /* the contract's block is room-keyed; a staff-column hold names its person */
          room: String(m.room || m.staffName || "Floor"),
          ...(day?.locationId ? { locationId: day.locationId } : {}),
          notes: m.staffName ? "Held for " + m.staffName + " (Floor Calendar)" : "Floor Calendar",
        });
        if (m.localId && created?.id) blockIdsRef.current[m.localId] = String(created.id);
        reload();
        return;
      }
      if (m.op === "blockRemove") {
        const sid = blockIdsRef.current[String(m.localId || "")];
        if (!sid) return fail("That block predates this session or never saved — nothing to delete on the server");
        await calendarDay.deleteCalendarDayBlock(sid);
        delete blockIdsRef.current[String(m.localId || "")];
        reload();
        return;
      }
      if (m.op === "dutyMove") {
        const bid = String(m.id || "");
        if (!bid.startsWith("qd")) return fail("That duty block was added on the board this session — it has no served roster row yet");
        const sid = (payload.dutyIds || [])[Number(bid.slice(2))];
        if (!sid) return fail("That duty block has no served roster row — nothing to move on the server");
        const st = staffOf(m.staffIdx);
        await moveQueueDuty(String(sid), { staff: st?.name ?? undefined, from: hm(Number(m.from) || 0), to: hm(Number(m.to) || 0) });
        reload();
        return;
      }
      if (m.op === "eventAssign") {
        const ev = day?.events.find((e) => e.id === m.eventId || e.studioId === m.eventId || staffKey(e.title) === staffKey(String(m.eventId || "")));
        const st = staffOf(m.staffIdx);
        if (!ev || !st?.staffId) return fail("Couldn't resolve the event or the person to a served identity");
        const plans = await listEventStaffing(dateForDay, dateForDay, day?.locationId ?? undefined);
        const plan = (plans?.items || []).find((x) => x.eventId === ev.id || staffKey(x.title) === staffKey(ev.title));
        const role = plan?.roles?.find((r) => (r.assignees?.length ?? 0) < (r.count ?? 1)) ?? plan?.roles?.[0];
        if (!plan || !role) return fail("No staffing plan is served for " + ev.title + " — the assignment is local only");
        await assignEventRole(plan.eventId, role.id, st.staffId);
        reload();
        return;
      }
      if (m.op === "convert") {
        const q = day?.queues.find((x) => x.id === m.queueId) || day?.queues[0];
        if (!q) return fail("No served queue to join — the conversion is local only");
        await addQueueEntry(q.id, { name: String(m.name || "Walk-in"), service: m.service || null });
        if (m.cancel && m.id) {
          const id = servedId(String(m.id));
          if (id) await cancelAppointment(id, "Converted to the walk-in queue");
          else fail("Queue joined, but the appointment was board-local — nothing to cancel on the server");
        }
        reload();
        return;
      }
    } catch (e) {
      fail(e instanceof Error ? e.message : String(e));
    }
  }, [day, dayOff, dateForDay, reload]);

  useEffect(() => { if (boardReady) push(); }, [boardReady, push]);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      /* ORIGIN GATE — re-applied 2026-09-08 after a pack reverted it, and it is
         not optional. `floor-write` below dispatches ten authenticated
         mutations; an ungated listener lets ANY window holding a handle to
         Studio drive them, and `postMessage(_, "*")` leaks the board's data to
         whatever ends up in that frame. The board is served from our own origin,
         so both halves are an exact match rather than an allowlist. */
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== frameRef.current?.contentWindow) return;
      const m = ev.data as { type?: string; off?: number; detail?: { surface?: string; params?: Record<string, unknown> } } | null;
      if (!m || typeof m !== "object") return;
      if (m.type === "floor-board-ready") { setBoardReady(true); return; }
      if (m.type === "floor-day") { setDayOff(Number(m.off) || 0); return; }
      if (m.type === "floor-write") { void writeBack(m as unknown as Parameters<typeof writeBack>[0]); return; }
      if (m.type === "floor-nav" && m.detail) {
        const p = m.detail.params || {};
        if (m.detail.surface === "eventday") {
          /* the board speaks its own event ids — map by title to the events family */
          const title = String((p.title as string) || "");
          const hit = day?.events.find((e) => e.studioId && (String(p.eventId) === e.id || (title && staffKey(e.title) === staffKey(title))));
          const sid = hit?.studioId || day?.events.find((e) => e.studioId)?.studioId;
          if (sid) nav.go("eventday", { eventId: sid });
          return;
        }
        if (m.detail.surface === "concierge") {
          const q = day?.queues.find((x) => String(p.queueId) === x.id || staffKey(x.name) === staffKey(String((p.queueName as string) || "")));
          nav.go("queues.console", q?.consoleId ? { q: q.consoleId } : {});
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [day, nav, writeBack]);

  return (
    <Screen rail="floor-calendar">
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <DataSourceStrip screen="floor.calendar" ops={["listAppointments", "getOpsBoard", "listQueueDuty", "listExternalBusy", "listResources"]} reqId="REQ-CAL-1" />
        {error && !day ? <EmptyState title={t("floorCal.pending", "The board couldn't load")} body={error} /> : null}
        {loading && !day ? (
          <div style={{ padding: 18, fontSize: 13.5, color: T.soft }}>{t("floorCal.loading", "Loading the board…")}</div>
        ) : null}
        <iframe ref={frameRef} title="Floor Calendar" src={boardSrc}
          onLoad={() => { /* ready message drives the first push; this is the reload fallback */ if (day) push(); }}
          style={{ flex: 1, minHeight: 0, width: "100%", border: "none", background: "#F4F5F7" }} />
      </div>
    </Screen>
  );
}
